'use client';

import { Copy, ExternalLink, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { PostQuestInput } from '@zealy/utils';
import { Alert, Button } from '@zealy/design-system';
import { RepeatOnLine } from '@zealy/icons';
import {
  extractUsernameFromInviteUrl,
  generateBotInviteUrl,
  useTelegramBotStatus,
} from '@zealy/queries';

import { envConfig } from '#app/config';
import { toast } from '#components/Toaster';
import { useDebouncedValue } from '#hooks/useDebouncedValue';

interface BotStatusFieldProps {
  name: string;
  namespace: string;
}

export const BotStatusField: React.FC<BotStatusFieldProps> = ({ name }) => {
  const { watch } = useFormContext();
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(false);

  // Get the invite URL for the same task by replacing botStatus with inviteUrl in the path
  const inviteUrlPath = name.replace('botStatus', 'inviteUrl');
  const inviteUrl = watch(inviteUrlPath);

  // Extract username from invite URL
  const username = React.useMemo(() => {
    if (!inviteUrl) return null;
    return extractUsernameFromInviteUrl(inviteUrl);
  }, [inviteUrl]);

  // Debounce username to prevent API calls on every keystroke
  const debouncedUsername = useDebouncedValue(username ?? '', 600);

  // Query bot status when debounced username is available
  const {
    data: botStatus,
    isLoading,
    error,
    refetch,
  } = useTelegramBotStatus({
    username: debouncedUsername || undefined,
  });

  // Generate admin invite URL
  const adminInviteUrl = React.useMemo(() => {
    if (!username) return null;
    return generateBotInviteUrl(username, envConfig.telegram.botUsername);
  }, [username]);

  // Check if bot status is invalid (not member or not admin)
  const isBotStatusInvalid = botStatus && (!botStatus.is_member || !botStatus.is_admin);

  // Show refresh button after 5 seconds when bot status is invalid
  useEffect(() => {
    if (isBotStatusInvalid && !isLoading && !error) {
      const timer = setTimeout(() => {
        setShowRefreshButton(true);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setShowRefreshButton(false);
    }
  }, [isBotStatusInvalid, isLoading, error]);

  // Cooldown duration in milliseconds
  const COOLDOWN_DURATION = 5000; // 5 seconds

  // Handle refresh action with network bypass and cooldown
  const handleRefresh = async () => {
    if (refreshCooldown || isRefreshing) return;

    setIsRefreshing(true);
    try {
      // Force network request bypassing cache
      await refetch({
        cancelRefetch: false,
        throwOnError: false,
      });
    } finally {
      setIsRefreshing(false);
      // Start cooldown period
      setRefreshCooldown(true);
      setTimeout(() => setRefreshCooldown(false), COOLDOWN_DURATION);
    }
  };

  const getTitle = () => (username ? `Bot Status (@${username})` : 'Bot Status');

  const handleCopyBotUsername = async () => {
    if (envConfig.telegram.botUsername) {
      try {
        await navigator.clipboard.writeText(`@${envConfig.telegram.botUsername}`);
        toast.success(`Bot username copied: @${envConfig.telegram.botUsername}`);
      } catch (copyError) {
        console.error('Failed to copy bot username:', copyError);
        toast.error('Failed to copy bot username');
      }
    }
  };

  const renderAlert = () => {
    if (!username && !!inviteUrl) {
      return (
        <Alert
          variant="error"
          title={getTitle()}
          description="Invalid Telegram invite URL format"
        />
      );
    }

    if (isLoading) {
      return (
        <Alert
          variant="info"
          title={getTitle()}
          description="Checking bot status..."
          icon={<Loader2 className="animate-spin w-alert-icon h-alert-icon" />}
        />
      );
    }

    if (error) {
      return (
        <Alert
          variant="error"
          title={getTitle()}
          description="Failed to check bot status"
          actions={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              className="text-brand-primary hover:text-brand-secondary"
            >
              Try again
            </Button>
          }
        />
      );
    }

    if (botStatus?.is_admin && botStatus?.is_member) {
      return (
        <Alert
          variant="success"
          title={getTitle()}
          description="Zealy bot is present with admin permissions"
        />
      );
    }

    if (!inviteUrl) {
      return <></>;
    }

    // Bot is not a member or lacks admin permissions - combined error state
    return (
      <Alert
        variant="error"
        title={getTitle()}
        description={
          envConfig.telegram.botUsername
            ? `Zealy bot (@${envConfig.telegram.botUsername}) is not present in this chat or lacks admin permissions. Invite the bot with admin permissions to enable automatic task validation.`
            : 'Zealy bot is not present in this chat or lacks admin permissions. Invite the bot with admin permissions to enable automatic task validation.'
        }
        actions={
          <div className="flex items-center gap-150">
            {showRefreshButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                isDisabled={isRefreshing || refreshCooldown}
                className="flex items-center gap-150"
                leftIcon={
                  isRefreshing ? (
                    <Loader2 className="animate-spin w-icon-sm h-icon-sm" />
                  ) : (
                    <RepeatOnLine className="w-icon-sm h-icon-sm" />
                  )
                }
              >
                {isRefreshing
                  ? 'Refreshing...'
                  : refreshCooldown
                  ? 'Refresh Status (5s)'
                  : 'Refresh Status'}
              </Button>
            )}
            {adminInviteUrl && (
              <Button
                variant="muted"
                size="sm"
                onClick={() => window.open(adminInviteUrl, '_blank')}
                className="flex items-center gap-150"
                leftIcon={<ExternalLink className="w-icon-sm h-icon-sm" />}
              >
                Invite Bot with Admin Rights
              </Button>
            )}
          </div>
        }
      />
    );
  };

  return <div className="space-y-300">{renderAlert()}</div>;
};
